import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getIncident, updateIncident, analyzeIncident, updateIncidentChecklist } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Brain, 
  Calendar, 
  MapPin, 
  Users,
  Loader2,
  CheckCircle,
  AlertTriangle,
  FileText,
  Shield,
  Scale,
  Target,
  Clock,
  AlertCircle,
  TrendingUp,
  ClipboardCheck,
  RefreshCw,
  ChevronRight,
  Zap,
  Building2,
  User
} from 'lucide-react';

// Risk Score Component
const RiskScoreGauge = ({ score }) => {
  const getColor = (s) => {
    if (s <= 3) return 'text-green-500';
    if (s <= 5) return 'text-yellow-500';
    if (s <= 7) return 'text-orange-500';
    return 'text-red-500';
  };
  
  const getBgColor = (s) => {
    if (s <= 3) return 'bg-green-500';
    if (s <= 5) return 'bg-yellow-500';
    if (s <= 7) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center gap-4">
      <div className={`text-4xl font-bold ${getColor(score)}`}>
        {score}/10
      </div>
      <div className="flex-1">
        <Progress value={score * 10} className={`h-3 ${getBgColor(score)}`} />
      </div>
    </div>
  );
};

// Liability Badge Component
const LiabilityBadge = ({ level }) => {
  const styles = {
    'Low': 'bg-green-500/20 text-green-500 border-green-500/30',
    'Medium': 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
    'High': 'bg-orange-500/20 text-orange-500 border-orange-500/30',
    'Critical': 'bg-red-500/20 text-red-500 border-red-500/30',
  };
  
  return (
    <Badge className={`${styles[level] || styles['Medium']} border text-sm px-3 py-1`}>
      {level} Liability
    </Badge>
  );
};

// Priority Badge
const PriorityBadge = ({ priority }) => {
  const styles = {
    'immediate': 'bg-red-500/20 text-red-400',
    'short_term': 'bg-yellow-500/20 text-yellow-400',
    'long_term': 'bg-blue-500/20 text-blue-400',
  };
  
  const labels = {
    'immediate': 'Immediate',
    'short_term': 'Short Term',
    'long_term': 'Long Term',
  };
  
  return (
    <Badge className={`${styles[priority] || styles['short_term']} text-xs`}>
      {labels[priority] || priority}
    </Badge>
  );
};

export default function IncidentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [resolution, setResolution] = useState('');
  const [status, setStatus] = useState('');
  const [checklistUpdating, setChecklistUpdating] = useState({});

  const fetchIncident = useCallback(async () => {
    try {
      const response = await getIncident(id);
      setIncident(response.data);
      setResolution(response.data.resolution || '');
      setStatus(response.data.status);
      
      // Auto-trigger analysis if not present and viewing for first time
      if (!response.data.ai_analysis) {
        triggerAutoAnalysis();
      }
    } catch (error) {
      toast.error('Failed to fetch incident');
      navigate('/incidents');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchIncident();
  }, [fetchIncident]);

  // Poll for analysis completion if analyzing
  useEffect(() => {
    let pollInterval;
    if (incident && !incident.ai_analysis && !analyzing) {
      // Check every 3 seconds if analysis is complete
      pollInterval = setInterval(async () => {
        try {
          const response = await getIncident(id);
          if (response.data.ai_analysis) {
            setIncident(response.data);
            clearInterval(pollInterval);
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, 3000);
    }
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [incident, analyzing, id]);

  const triggerAutoAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await analyzeIncident(id);
      setIncident(prev => ({ ...prev, ai_analysis: response.data.analysis }));
      toast.success('AI analysis complete');
    } catch (error) {
      console.error('Auto-analysis failed:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReanalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await analyzeIncident(id);
      setIncident(prev => ({ ...prev, ai_analysis: response.data.analysis }));
      toast.success('AI analysis refreshed');
    } catch (error) {
      toast.error('Failed to analyze incident');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await updateIncident(id, { status, resolution });
      toast.success('Incident updated');
      fetchIncident();
    } catch (error) {
      toast.error('Failed to update incident');
    } finally {
      setUpdating(false);
    }
  };

  const handleChecklistToggle = async (itemId, currentCompleted) => {
    setChecklistUpdating(prev => ({ ...prev, [itemId]: true }));
    try {
      const response = await updateIncidentChecklist(id, itemId, !currentCompleted);
      // Update local state
      setIncident(prev => ({
        ...prev,
        ai_analysis: {
          ...prev.ai_analysis,
          structured: {
            ...prev.ai_analysis.structured,
            action_checklist: response.data.checklist
          }
        }
      }));
      toast.success(currentCompleted ? 'Task marked incomplete' : 'Task completed!');
    } catch (error) {
      toast.error('Failed to update checklist');
    } finally {
      setChecklistUpdating(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const getSeverityClass = (severity) => {
    const classes = {
      critical: 'severity-critical',
      high: 'severity-high',
      medium: 'severity-medium',
      low: 'severity-low',
    };
    return classes[severity] || '';
  };

  const getStatusClass = (status) => {
    const classes = {
      open: 'status-open',
      'new': 'status-open',
      'in-progress': 'status-in-progress',
      'in_review': 'status-in-progress',
      resolved: 'status-resolved',
      closed: 'status-resolved',
      imported: 'status-imported',
    };
    return classes[status] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  if (!incident) {
    return null;
  }

  const analysis = incident.ai_analysis?.structured || null;
  const checklist = analysis?.action_checklist || [];
  const completedCount = checklist.filter(item => item.completed).length;
  const checklistProgress = checklist.length > 0 ? (completedCount / checklist.length) * 100 : 0;

  return (
    <div data-testid="incident-detail-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/incidents')}
          className="mt-1"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
              {incident.title}
            </h1>
            <Badge className={`${getSeverityClass(incident.severity)} uppercase`}>
              {incident.severity}
            </Badge>
            <Badge className={`${getStatusClass(incident.status)} uppercase`}>
              {incident.status}
            </Badge>
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(incident.incident_date).toLocaleDateString()}
            </span>
            {incident.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {incident.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              {incident.primary_category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || incident.category}
            </span>
          </div>
        </div>
        <Button
          onClick={handleReanalyze}
          disabled={analyzing}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          {analyzing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Re-analyze
        </Button>
      </div>

      {/* Analysis Loading State */}
      {!analysis && (
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <Brain className="w-16 h-16 text-primary animate-pulse" />
                <Loader2 className="w-8 h-8 absolute -bottom-1 -right-1 text-primary animate-spin" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">AI Analysis in Progress</h3>
                <p className="text-muted-foreground">
                  Our AI is analyzing this incident to provide risk assessment, root cause analysis, and recommended actions...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analysis Cards */}
      {analysis && (
        <>
          {/* Summary Card */}
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-primary/20">
                  <Brain className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-2">AI Analysis Summary</h3>
                  <p className="text-muted-foreground">{analysis.summary}</p>
                  <p className="text-xs text-muted-foreground mt-3">
                    Analyzed: {new Date(incident.ai_analysis.analyzed_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Assessment Card */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-orange-500/10">
                    <Target className="w-5 h-5 text-orange-500" />
                  </div>
                  <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
                    Risk Assessment
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Overall Risk Score</p>
                  <RiskScoreGauge score={analysis.risk_assessment?.overall_risk_score || 5} />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Liability Level</span>
                  <LiabilityBadge level={analysis.risk_assessment?.liability_level || 'Medium'} />
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Insurance Impact</p>
                  <p className="text-sm">{analysis.risk_assessment?.insurance_impact}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Reputation Risk</p>
                  <p className="text-sm">{analysis.risk_assessment?.reputation_risk}</p>
                </div>
              </CardContent>
            </Card>

            {/* Root Cause Analysis Card */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                  </div>
                  <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
                    Root Cause Analysis
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <p className="text-sm text-muted-foreground mb-1">Primary Cause</p>
                  <p className="font-medium">{analysis.root_cause_analysis?.primary_cause}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Contributing Factors</p>
                  <ul className="space-y-2">
                    {analysis.root_cause_analysis?.contributing_factors?.map((factor, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <ChevronRight className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Systemic Issues</p>
                  <p className="text-sm">{analysis.root_cause_analysis?.systemic_issues}</p>
                </div>
              </CardContent>
            </Card>

            {/* Liability Concerns Card */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <Scale className="w-5 h-5 text-red-500" />
                  </div>
                  <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
                    Liability Concerns
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                  <p className="text-sm text-muted-foreground mb-1">Legal Exposure</p>
                  <p className="text-sm">{analysis.liability_concerns?.legal_exposure}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Compliance Gaps</p>
                  <ul className="space-y-2">
                    {analysis.liability_concerns?.compliance_gaps?.map((gap, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        {gap}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Documentation Needs</p>
                  <p className="text-sm">{analysis.liability_concerns?.documentation_needs}</p>
                </div>
              </CardContent>
            </Card>

            {/* Action Checklist Card */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <ClipboardCheck className="w-5 h-5 text-green-500" />
                    </div>
                    <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
                      Admin Action Checklist
                    </CardTitle>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {completedCount}/{checklist.length} Complete
                  </Badge>
                </div>
                <div className="mt-3">
                  <Progress value={checklistProgress} className="h-2" />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {checklist.map((item) => (
                  <div 
                    key={item.id} 
                    className={`p-3 rounded-lg border transition-all ${
                      item.completed 
                        ? 'bg-green-500/5 border-green-500/20' 
                        : 'bg-secondary/30 border-border hover:border-primary/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={item.completed}
                        onCheckedChange={() => handleChecklistToggle(item.id, item.completed)}
                        disabled={checklistUpdating[item.id]}
                        className="mt-0.5"
                        data-testid={`checklist-item-${item.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <PriorityBadge priority={item.priority} />
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {item.deadline}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {item.responsible}
                          </span>
                        </div>
                        <p className={`text-sm ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
                          {item.action}
                        </p>
                        {item.completed && item.completed_at && (
                          <p className="text-xs text-green-500 mt-1">
                            ✓ Completed {new Date(item.completed_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {checklistUpdating[item.id] && (
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Incident Description */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
                Incident Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {incident.description}
              </p>
            </CardContent>
          </Card>

          {/* Subject Information */}
          {incident.subject_name && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Subject Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <p className="font-medium">{incident.subject_name}</p>
                  </div>
                  {incident.subject_age_approximate && (
                    <div>
                      <span className="text-muted-foreground">Age Range:</span>
                      <p className="font-medium">{incident.subject_age_approximate.replace('_', ' ')}</p>
                    </div>
                  )}
                  {incident.subject_affiliation && (
                    <div>
                      <span className="text-muted-foreground">Affiliation:</span>
                      <p className="font-medium">{incident.subject_affiliation}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Involved Parties */}
          {incident.involved_parties?.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Involved Parties
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {incident.involved_parties.map((party, index) => (
                    <Badge key={index} variant="outline">{party}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Update Status */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
                Update Incident
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="in_review">In Review</SelectItem>
                    <SelectItem value="action_required">Action Required</SelectItem>
                    <SelectItem value="escalated">Escalated</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Resolution Notes</label>
                <Textarea
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  placeholder="Document how this incident was resolved..."
                  rows={4}
                  className="bg-secondary/50"
                />
              </div>
              <Button
                onClick={handleUpdate}
                disabled={updating}
                className="w-full btn-primary-glow"
                data-testid="update-incident-btn"
              >
                {updating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Update Incident
              </Button>
            </CardContent>
          </Card>

          {/* Details */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
                Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Created</span>
                <span>{new Date(incident.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Category</span>
                <span>{incident.secondary_type || incident.category}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Near Miss</span>
                <span>{incident.is_near_miss ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Reported to Auth.</span>
                <span>{incident.reported_to_authorities ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Witnesses</span>
                <span>{incident.witnesses?.filter(w => w).length || 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
